import React, { useEffect, useState } from 'react';
import { X, StickyNote, AlertCircle, Lock, Loader, Info } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { pickingService } from '../../../../services/pickingService';
import { PListLineDto, PListLineMemo, PListLineMemoField } from '../../../../types/picking';

interface LineMemoModalProps {
  isOpen: boolean;
  pListId: number;
  line: PListLineDto | null;
  /** Operatøren som har listen låst akkurat nå — endring er tillatt, men vises som info. */
  lockedBy?: string | null;
  onClose: () => void;
  onSaved?: () => void;
}

/**
 * «Memo» på en plukklistelinje (Haucon 2026-10-07). Viser infofeltene plukkerne har satt opp i
 * profilen (infofelt 1–3) med verdien fra ordrelinjen, og lar lagersjefen legge til, endre eller
 * fjerne tekst i fritekstfeltene — som planleggeren i bizClick. Plukkeren ser teksten på linjen
 * (infofelt 2 i rødt). Teksten er bare informasjon til plukker og går ikke tilbake til ERP.
 */
export const LineMemoModal: React.FC<LineMemoModalProps> = ({
  isOpen,
  pListId,
  line,
  lockedBy,
  onClose,
  onSaved
}) => {
  const { t } = useTranslation();
  const k = 'manager.modules.newPickingLists.memo';
  const [memo, setMemo] = useState<PListLineMemo | null>(null);
  const [values, setValues] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !line) return;
    let cancelled = false;
    setMemo(null);
    setValues({});
    setError(null);
    setSaving(false);
    setLoading(true);
    pickingService.getLineMemo(pListId, line.pListLineNo)
      .then(m => {
        if (cancelled) return;
        setMemo(m);
        setValues(Object.fromEntries(m.fields.filter(f => f.editable).map(f => [f.fieldName, f.value ?? ''])));
      })
      .catch(err => {
        if (!cancelled) setError(err instanceof Error ? err.message : t(`${k}.loadError`, {}, 'Kunne ikke hente memo'));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, line, pListId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen || !line) return null;

  const fieldLabel = (f: PListLineMemoField) =>
    f.label?.trim() || t(`${k}.infoField`, { '0': f.slot }, 'Infofelt {0}');

  const editableFields = memo?.fields.filter(f => f.editable) ?? [];
  const readOnlyFields = memo?.fields.filter(f => !f.editable) ?? [];
  const changed = editableFields.filter(f => (values[f.fieldName] ?? '').trim() !== (f.value ?? '').trim());
  const canSave = !!memo?.canEdit && changed.length > 0 && !saving && !loading;

  const handleSave = async () => {
    if (!canSave) return;
    try {
      setSaving(true);
      setError(null);
      const payload: Record<string, string | null> = {};
      for (const f of changed) {
        const v = (values[f.fieldName] ?? '').trim();
        payload[f.fieldName] = v === '' ? null : v;
      }
      await pickingService.updateLineMemo(pListId, line.pListLineNo, payload);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t(`${k}.saveError`, {}, 'Kunne ikke lagre memo'));
    } finally {
      setSaving(false);
    }
  };

  const visibilityHint = (f: PListLineMemoField) =>
    f.totalProfiles > 0 && f.profileCount < f.totalProfiles
      ? t(`${k}.shownFor`, { '0': f.profileCount, '1': f.totalProfiles }, 'Vises for {0} av {1} operatørprofiler')
      : null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <StickyNote size={18} className="text-gray-600" />
            {t(`${k}.title`, {}, 'Memo til plukker')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label={t('common.buttons.close', {}, 'Lukk')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="px-4 py-4 space-y-4 overflow-y-auto">
          {/* Linjeinfo */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm space-y-1">
            <div className="font-mono font-semibold text-gray-900">{line.itemNo}</div>
            {line.itemDescription && <div className="text-gray-700">{line.itemDescription}</div>}
            <div className="text-gray-600">
              {t('manager.modules.newPickingLists.changeQuantity.order', { '0': line.orderNoERP || String(line.orderNo), '1': String(line.lineNoERP ?? line.lineNo) }, `Ordre ${line.orderNoERP || line.orderNo}, linje ${line.lineNoERP ?? line.lineNo}`)}
            </div>
          </div>

          {lockedBy && memo?.canEdit && (
            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <Lock size={16} className="flex-shrink-0 mt-0.5" />
              <span>{t(`${k}.lockedInfo`, { '0': lockedBy }, 'Listen plukkes nå av {0}. Operatøren ser memoen når plukkbildet oppdateres.')}</span>
            </div>
          )}

          {loading && (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader size={16} className="animate-spin" />
              {t('common.status.loading', {}, 'Laster...')}
            </div>
          )}

          {memo && !memo.canEdit && memo.blockedReason && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
              <Info size={16} className="flex-shrink-0 mt-0.5" />
              <span>{memo.blockedReason}</span>
            </div>
          )}

          {memo && memo.fields.length === 0 && (
            <p className="text-sm text-gray-500">
              {t(`${k}.noFields`, {}, 'Ingen infofelt er satt opp for plukkerne. Infofeltene velges i operatørprofilen.')}
            </p>
          )}

          {/* Redigerbare infofelt */}
          {editableFields.map(f => {
            const hint = visibilityHint(f);
            const value = values[f.fieldName] ?? '';
            const inputId = `line-memo-${f.fieldName}`;
            return (
              <div key={f.fieldName}>
                <div className="flex items-baseline justify-between gap-2 mb-1">
                  <label className="block text-sm font-medium text-gray-700" htmlFor={inputId}>
                    {fieldLabel(f)}
                    <span className="ml-2 text-xs font-normal text-gray-400 font-mono">{f.fieldName}</span>
                  </label>
                  {value !== '' && memo?.canEdit && (
                    <button
                      type="button"
                      onClick={() => setValues(v => ({ ...v, [f.fieldName]: '' }))}
                      className="text-sm text-red-600 hover:underline min-h-[44px] px-2"
                    >
                      {t(`${k}.clear`, {}, 'Fjern tekst')}
                    </button>
                  )}
                </div>
                <textarea
                  id={inputId}
                  value={value}
                  onChange={(e) => setValues(v => ({ ...v, [f.fieldName]: e.target.value }))}
                  maxLength={f.maxLength ?? undefined}
                  rows={2}
                  disabled={!memo?.canEdit || saving}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white disabled:bg-gray-50 disabled:text-gray-500"
                  placeholder={t(`${k}.placeholder`, {}, 'Skriv tekst til plukker')}
                />
                <div className="flex justify-between gap-2 mt-1 text-xs text-gray-500">
                  <span>{hint ?? ''}</span>
                  {f.maxLength && <span className="tabular-nums">{value.length}/{f.maxLength}</span>}
                </div>
              </div>
            );
          })}

          {/* Skrivebeskyttede infofelt (ERP-/varedata) */}
          {readOnlyFields.length > 0 && (
            <div className="border-t border-gray-200 pt-3 space-y-2">
              <p className="text-xs text-gray-500">{t(`${k}.readOnlyInfo`, {}, 'Disse infofeltene kommer fra ERP og kan ikke endres her.')}</p>
              {readOnlyFields.map(f => (
                <div key={f.fieldName} className="text-sm">
                  <div className="text-gray-700 font-medium">
                    {fieldLabel(f)}
                    <span className="ml-2 text-xs font-normal text-gray-400 font-mono">{f.fieldName}</span>
                  </div>
                  <div className="text-gray-900 whitespace-pre-wrap break-words">{f.value || '–'}</div>
                </div>
              ))}
            </div>
          )}

          {editableFields.length > 0 && (
            <p className="text-xs text-gray-500">
              {t(`${k}.erpInfo`, {}, 'Teksten lagres på ordrelinjen og vises for plukker. Den sendes ikke tilbake til ERP.')}
            </p>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 min-h-[44px]"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className={`px-4 py-2 rounded-lg font-medium min-h-[44px] flex items-center gap-2 ${
              canSave ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            {saving && <Loader size={16} className="animate-spin" />}
            {t('common.buttons.save', {}, 'Lagre')}
          </button>
        </div>
      </div>
    </div>
  );
};
